import { useContext, useEffect, useState } from 'react'
import { AppContext } from '../../App'
import Chart from './chart'
import { subscribeChartRegistry } from './chartRegistryEvents'

/**
 * `setChart` runs before `chartApi` is attached on first mount, so a registration
 * event alone is not enough to resolve the instance. Retry for a short window, then
 * fall back to registry events rather than polling a pane that has no candle chart
 * at all (multistrike, OI, option chain).
 */
const MAX_ATTACH_FRAMES = 120

export function useAppContextChart(viewKey: string): Chart | undefined {
    const appContext = useContext(AppContext)
    const [chart, setChart] = useState<Chart | undefined>(() => {
        const existing = viewKey ? appContext.getChart(viewKey) : undefined
        return existing?.chartApi ? existing : undefined
    })

    useEffect(() => {
        if (!viewKey) {
            setChart(undefined)
            return
        }

        let cancelled = false
        let frame: number | undefined
        let framesLeft = MAX_ATTACH_FRAMES

        const clearFrame = () => {
            if (frame !== undefined) {
                cancelAnimationFrame(frame)
                frame = undefined
            }
        }

        const resolve = () => {
            if (cancelled) return
            const next = appContext.getChart(viewKey)
            const resolved = next?.chartApi ? next : undefined
            setChart((prev) => (prev === resolved ? prev : resolved))
            if (resolved || framesLeft <= 0) {
                clearFrame()
                return
            }
            if (frame !== undefined) return
            framesLeft -= 1
            frame = requestAnimationFrame(() => {
                frame = undefined
                resolve()
            })
        }

        const onRegistryChange = () => {
            framesLeft = MAX_ATTACH_FRAMES
            resolve()
        }

        resolve()
        const unsubscribe = subscribeChartRegistry(onRegistryChange)

        return () => {
            cancelled = true
            clearFrame()
            unsubscribe()
        }
    }, [appContext, viewKey])

    return chart
}
